
import { unByKey } from 'ol/Observable';
import { LineString, Polygon } from 'ol/geom';
import Draw from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import Overlay from 'ol/Overlay';
import { transform } from 'ol/proj';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { distance } from 'ol/coordinate';
import Snap from 'ol/interaction/Snap';
import { map } from './Map';
import { snapSource } from './Snapping';
import { setFloatingControlsVisible, closeSidebarOnMobile } from './CustomFunctions';

/* =========================
   طبقة القياس (ستايل أجمل)
   ========================= */
const source = new VectorSource();

const displayStyle = (feature) => {
  const geom = feature.getGeometry();
  const styles = [];

  const pointStyle = new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: '#ffffff' }),
      stroke: new Stroke({ color: '#0015ffff', width: 3 })
    })
  });

  if (geom instanceof LineString) {
    styles.push(
      new Style({
        stroke: new Stroke({
          color: '#0800ffff',
          width: 3,
          lineCap: 'round',
          lineJoin: 'round'
        })
      })
    );
    styles.push(pointStyle);
  } else if (geom instanceof Polygon) {
    styles.push(
      new Style({
        fill: new Fill({ color: 'rgba(220,53,69,0.12)' }),
        stroke: new Stroke({ color: '#0d00ffff', width: 3 })
      })
    );
  } else {
    styles.push(pointStyle);
  }

  return styles;
};

const vector = new VectorLayer({
  source: source,
  style: displayStyle,
  name: 'MeasureLayer'
});

map.addLayer(vector);

// Snap interaction تلقائي على الأجزاء المقاسة - يعمل دائماً على vertex فقط
// هذا Snap يعمل على الأجزاء المقاسة نفسها
const measureSnapInteraction = new Snap({
  source: source,
  pixelTolerance: 15,
  edge: false,  // تعطيل التجاذب على الأضلاع
  vertex: true  // تفعيل التجاذب فقط على النقاط
});
map.addInteraction(measureSnapInteraction);

// Snap interaction إضافي على الأجزاء المرسومة (cross-snapping)
// ليعمل snap على الأجزاء المرسومة عند القياس - يعمل دائماً
let measureSnapInteractionDraw = null;
function initMeasureSnapToDraw() {
  // الحصول على drawSource من Draw.js
  if (typeof window.drawSourceForSnap === 'function') {
    const drawSrc = window.drawSourceForSnap();
    if (drawSrc) {
      if (measureSnapInteractionDraw) {
        map.removeInteraction(measureSnapInteractionDraw);
      }
      measureSnapInteractionDraw = new Snap({
        source: drawSrc,
        pixelTolerance: 15,
        edge: false,  // تعطيل التجاذب على الأضلاع
        vertex: true  // تفعيل التجاذب فقط على النقاط
      });
      map.addInteraction(measureSnapInteractionDraw);
    }
  }
}

// تهيئة cross-snap عند بدء القياس
function ensureMeasureCrossSnap() {
  if (!measureSnapInteractionDraw) {
    initMeasureSnapToDraw();
  }
}

// ⚡ [LATE-BINDING] - Snapping will resolve its source on-demand during activation

/* =========================
   متغيّرات عامة
   ========================= */
let sketch;
let helpTooltipElement;
let helpTooltip;
let measureTooltipElement;
let measureTooltip;
let draw;
let listener;
let measureType = 'LineString';
let isMeasuring = false;
let measurements = [];

// نقاط الإحداثيات
let pointCounter = 1;
let coordinatePoints = [];

/* =========================
   عدسة مكبرة (Canvas-based Magnifier)
   ========================= */
const MAGNIFIER_SIZE = 90;   // px
const LONG_PRESS_MS = 300;    // ms

let magnifier = null;
let magnifierActive = false;
let magnifierCenterPixel = null;
let longPressTimer = null;
// متغير عالمي لمنع ظهور العدسة عند فتح الشريط الجانبي
window.magnifierSuppressed = false;
let _measureMoveEndHandler = null;
let _measurePostRenderHandler = null;

/**
 * الحصول على أقرب vertex (نقطة رأس) فقط من الشكل
 * @param {ol.geom.Geometry} geometry - الشكل الهندسي
 * @param {ol.Coordinate} coordinate - الإحداثيات المستهدفة
 * @returns {ol.Coordinate|null} أقرب vertex أو null
 */
function getClosestVertex(geometry, coordinate) {
  if (!geometry || !coordinate) return null;
  if (geometry.getType() === 'Point') return geometry.getCoordinates();

  const flats = geometry.getFlatCoordinates ? geometry.getFlatCoordinates() : null;
  const stride = geometry.getStride ? geometry.getStride() : null;
  if (!flats || !stride) return geometry.getClosestPoint(coordinate);

  let closest = null;
  let minDistSq = Infinity;
  const cx = coordinate[0], cy = coordinate[1];

  for (let i = 0; i < flats.length; i += stride) {
    const vx = flats[i];
    const vy = flats[i + 1];
    const dx = vx - cx;
    const dy = vy - cy;
    const distSq = dx * dx + dy * dy;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      closest = [vx, vy];
    }
  }
  return closest;
}

function createMagnifier() {
  // افحص إذا كانت العدسة موجودة
  let existing = document.getElementById('map-magnifier');
  if (existing) {
    magnifier = existing;
    // تحديث الاستايل للتأكيد
    Object.assign(magnifier.style, {
      width: MAGNIFIER_SIZE + 'px',
      height: MAGNIFIER_SIZE + 'px',
      display: 'none'
    });
    return;
  }

  magnifier = document.createElement('div');
  magnifier.id = 'map-magnifier';
  Object.assign(magnifier.style, {
    width: MAGNIFIER_SIZE + 'px',
    height: MAGNIFIER_SIZE + 'px',
    zIndex: 1900,
    display: 'none'
  });

  // علامة تصويب هندسية دقيقة
  if (!magnifier.querySelector('.magnifier-crosshair')) {
    const cross = document.createElement('div');
    cross.className = 'magnifier-crosshair';
    
    const centerDot = document.createElement('div');
    centerDot.className = 'magnifier-center-dot';
    
    magnifier.appendChild(cross);
    magnifier.appendChild(centerDot);
  }

  // مؤشر السناب (الدائرة البرتقالية) كعنصر DOM
  if (!magnifier.querySelector('#map-snap-indicator')) {
    const snapCircle = document.createElement('div');
    snapCircle.id = 'map-snap-indicator';
    Object.assign(snapCircle.style, {
      position: 'absolute',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      background: 'rgba(255, 140, 0, 0.8)',
      border: '2px solid #fff',
      display: 'none',
      zIndex: 3,
      transform: 'translate(-50%, -50%)'
    });
    magnifier.appendChild(snapCircle);
  }

  document.body.appendChild(magnifier);
  // تأكد من إزالة أي عدسة قديمة للمساعدة في تنظيف الـ DOM
  if (document.getElementById('draw-magnifier')) {
    $('#draw-magnifier').remove();
  }
}
// كاش للأداء
// كاش للأداء
let cachedMeasureViewport = null;
let cachedMeasureMapCanvas = null;
let magnifierRenderPending = false;
let magnifierLastPixel = null;

function showMagnifierAt(pixel) {
  if (!magnifier) createMagnifier();
  magnifierLastPixel = pixel;
  if (!magnifierRenderPending) {
    magnifierRenderPending = true;
    requestAnimationFrame(renderMagnifier);
  }
}

// متغيرات السناب (المغناطيس)
let currentSnappedCoord = null;
const SNAP_THRESHOLD = 20; // بكسل

function renderMagnifier() {
  magnifierRenderPending = false;
  if (typeof isMeasuring !== 'undefined' && !isMeasuring) { hideMagnifier(); return; }

  const pixel = magnifierLastPixel;
  if (!pixel || !magnifier) return;

  cachedMeasureViewport = map.getViewport();
  const mapRect = cachedMeasureViewport.getBoundingClientRect();
  const clientX = mapRect.left + pixel[0];
  const clientY = mapRect.top + pixel[1];

  // وضع المؤشر
  magnifier.style.transform = `translate(-50%, -50%)`;
  magnifier.style.left = `${clientX}px`;
  magnifier.style.top = `${clientY}px`;
  magnifier.style.display = 'block';



  // ─────────────────────────────────────────────────────────────
  // منطق السناب (المغناطيس)
  // ─────────────────────────────────────────────────────────────
  currentSnappedCoord = null;
  let snapPixel = null;
  const snapIndicator = document.getElementById('map-snap-indicator');

  if (window.isSnappingEnabled) {
    const res = map.getView().getResolution();
    if (res <= (window.MAX_SNAP_RESOLUTION || 0.28)) {
      const mapCenterCoord = map.getCoordinateFromPixel(pixel);
      let closestPoint = null;
      let minDist = Infinity;

      if (mapCenterCoord) {
        const buffer = res * SNAP_THRESHOLD;
        const searchExtent = [
          mapCenterCoord[0] - buffer, mapCenterCoord[1] - buffer,
          mapCenterCoord[0] + buffer, mapCenterCoord[1] + buffer
        ];

        const findInSource = (src) => {
          if (!src) return;
          const featuresInBox = src.getFeaturesInExtent(searchExtent);
          for (let i = 0; i < featuresInBox.length; i++) {
            const f = featuresInBox[i];
            if (f.getGeometry().getType() === 'Point' && f.get('text')) continue;
            const v = getClosestVertex(f.getGeometry(), mapCenterCoord);
            if (v) {
              const dx = v[0] - mapCenterCoord[0];
              const dy = v[1] - mapCenterCoord[1];
              const d = Math.sqrt(dx * dx + dy * dy);
              if (d < minDist) {
                minDist = d;
                closestPoint = v;
              }
            }
          }
        };

        findInSource(snapSource);
        findInSource(source);
        if (typeof window.drawSourceForSnap === 'function') findInSource(window.drawSourceForSnap());

        if (closestPoint) {
          const closestPixel = map.getPixelFromCoordinate(closestPoint);
          const distPx = Math.sqrt(
            Math.pow(closestPixel[0] - pixel[0], 2) +
            Math.pow(closestPixel[1] - pixel[1], 2)
          );

          if (distPx < SNAP_THRESHOLD) {
            currentSnappedCoord = closestPoint;
            snapPixel = closestPixel;
            if (!window.lastSnapState && navigator.vibrate) navigator.vibrate(15);
            window.lastSnapState = true;
          } else {
            window.lastSnapState = false;
          }
        } else {
          window.lastSnapState = false;
        }
      } else {
        window.lastSnapState = false;
      }
    } else {
      window.lastSnapState = false;
    }
  }

  // تحديث موقع مؤشر السناب
  if (snapIndicator) {
    if (snapPixel) {
      const dx = snapPixel[0] - pixel[0];
      const dy = snapPixel[1] - pixel[1];
      snapIndicator.style.left = (MAGNIFIER_SIZE / 2 + dx) + 'px';
      snapIndicator.style.top = (MAGNIFIER_SIZE / 2 + dy) + 'px';
      snapIndicator.style.display = 'block';
    } else {
      snapIndicator.style.display = 'none';
    }
  }

  magnifierActive = true;
  magnifierCenterPixel = pixel.slice();
}


function hideMagnifier() {
  if (!magnifier) return;
  magnifier.style.display = 'none';
  magnifierActive = false;
  magnifierCenterPixel = null;
}

const isMobile = () => window.innerWidth <= 1024 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));

/* ربط أحداث اللمس على الـ viewport */
(function attachMagnifierHandlers() {
  const viewport = map && map.getViewport ? map.getViewport() : null;
  if (!viewport) return;
  if (viewport._magnifierBound) return;
  viewport._magnifierBound = true;

  // للموبايل: نثبت العدسة في المنتصف عند الرسم/القياس
  map.on('pointermove', (ev) => {
    if (!isMeasuring || !isMobile() || magnifierActive) return;
    // لا نحتاج للضغط المطول على الموبايل الآن، ستظهر العدسة المركزية
  });

  viewport.addEventListener('touchstart', (ev) => {
    if (!isMeasuring || isMobile()) return; // تخطي الضغط المطول على الجوال (سنستخدم المركز)
    if (ev.touches.length !== 1) return;
    const t = ev.touches[0];
    const r = viewport.getBoundingClientRect();
    const px = [t.clientX - r.left, t.clientY - r.top];
    longPressTimer = setTimeout(() => { showMagnifierAt(px); }, LONG_PRESS_MS);
  }, { passive: true });

  viewport.addEventListener('touchmove', (ev) => {
    if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
    if (!magnifierActive || isMobile() || ev.touches.length !== 1) return;
    const t = ev.touches[0];
    const r = viewport.getBoundingClientRect();
    const px = [t.clientX - r.left, t.clientY - r.top];
    showMagnifierAt(px);
  }, { passive: true });

  viewport.addEventListener('touchend', () => {
    if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
    if (!isMobile()) hideMagnifier();
  }, { passive: true });
})();

// وظائف التحكم بالجوال
function setupMobileHandlers() {
  $('#btnMobileAddPoint').off('click').on('click', () => {
    if (!isMeasuring) return;

    // استخدام النقطة الملقوطة (Snapped) إذا وجدت، وإلا استخدام مركز الخريطة
    const center = currentSnappedCoord ? currentSnappedCoord : map.getView().getCenter();

    // حالة إحداثيات النقاط
    if (measureType === 'Point') {
      const transformed = safeTransform(center, map.getView().getProjection(), 'EPSG:28191');
      const x = Math.round(transformed[0] * 100) / 100;
      const y = Math.round(transformed[1] * 100) / 100;
      createPermanentCoordinatePoint(center, x, y, pointCounter);
      saveCoordinateMeasurement(x, y, pointCounter);
      pointCounter++;
    } else if (draw) {
      // استخدام appendCoordinates يضمن تحديث الحالة الداخلية لـ OpenLayers
      if (draw.appendCoordinates) {
        draw.appendCoordinates([center]);
      }
    }

    if (navigator.vibrate) navigator.vibrate(20);
  });

  $('#btnMobileUndo').off('click').on('click', () => {
    if (draw) draw.removeLastPoint();
  });

  $('#btnMobileDone').off('click').on('click', () => {
    if (measureType !== 'Point' && draw) {
      draw.finishDrawing();
    }
    // إخفاء الأدوات والعدسة عند الانتهاء
    stopMeasuring($('#measure'));
    toggleMobileControls(false);
  });
}

function toggleMobileControls(show) {
  const controls = $('#mobileToolControls');
  if (show && isMobile()) {
    controls.fadeIn(300);
    setupMobileHandlers();
    // تحديث النتيجة في البار السفلي
    updateMobileToolValue();
  } else {
    controls.fadeOut(300);
  }
}

function updateMobileToolValue() {
  if (!isMeasuring) return;
  const label = measureType === 'Polygon' ? 'المساحة' : (measureType === 'LineString' ? 'المسافة' : 'الإحداثيات');
  $('.tool-label').text(label);

  // استخدم النقطة الملقوطة (Snapped) إذا وجدت، وإلا استخدم مركز الخريطة
  const effectiveCenter = currentSnappedCoord ? currentSnappedCoord : map.getView().getCenter();

  // في حالة عدم البدء بالرسم أو قياس النقاط
  if (measureType === 'Point' || !sketch) {
    const transformed = safeTransform(effectiveCenter, map.getView().getProjection(), 'EPSG:28191');
    const x = Math.round(transformed[0] * 100) / 100;
    const y = Math.round(transformed[1] * 100) / 100;
    $('#mobileToolValue').text(`X: ${x}, Y: ${y}`);
    return;
  }

  // 📐 الحساب المباشر أثناء الرسم (Live Update)
  const geom = sketch.getGeometry().clone();
  
  if (geom instanceof LineString) {
    geom.appendCoordinate(effectiveCenter);
    $('#mobileToolValue').text(formatLength(geom));
  } else if (geom instanceof Polygon) {
    const coords = geom.getCoordinates()[0] || [];
    if (coords.length >= 2) {
      const tempCoords = [...coords];
      // إذا كانت آخر نقطة هي نقطة البداية (مضلع مغلق)، نقوم بحذفها مؤقتاً لإضافة نقطة الماوس/المركز
      if (tempCoords.length > 2 && 
          tempCoords[0][0] === tempCoords[tempCoords.length-1][0] && 
          tempCoords[0][1] === tempCoords[tempCoords.length-1][1]) {
        tempCoords.pop();
      }
      tempCoords.push(effectiveCenter);
      tempCoords.push(tempCoords[0]); // إغلاق المضلع وهمياً لحساب المساحة
      geom.setCoordinates([tempCoords]);
      $('#mobileToolValue').text(formatArea(geom));
    }
  }
}


/* =========================
   زر "إظهار أداة القياس" المعلّق
   ========================= */
(function ensureMeasureShowChip() {
  if ($('#measureShowChip').length) return;
  const chip = $(`
    <div id="measureShowChip">
      <button type="button" id="measureShowChipBtn">
        <i class="fas fa-ruler"></i> إظهار أداة القياس
      </button>
    </div>
  `);
  $('body').append(chip);
  $('#measureShowChipBtn').on('click', () => { $('#measureModal').css('display', 'flex'); $('#measureShowChip').hide(); });
})();

/* =========================
   تصغير المودال الخاص بالقياس - الأنماط الآن في components.css
   ========================= */

/* =========================
   زر القياس في الشريط الجانبي
   ========================= */
$('#measure').off('click').on('click', function (e) {
  e.preventDefault();
  const button = $(this);
  const section = $('#measureSection');
  if (!isMeasuring) {
    startMeasuring(button);
    section.slideDown(200);
  } else {
    stopMeasuring(button);
    section.slideUp(200);
  }
});

/* =========================
   تشغيل/إيقاف القياس
   ========================= */
function startMeasuring(button) {
  isMeasuring = true;
  window.GeoPST.state.isMeasuring = isMeasuring;
  button.addClass('active');
  button.find('span').text('إيقاف القياس');
  button.find('i').removeClass('fa-ruler').addClass('fa-times');

  // إخفاء عدسة الرسم عند بدء القياس لمنع التداخل
  const drawMagnifier = document.getElementById('draw-magnifier');
  if (drawMagnifier) drawMagnifier.style.display = 'none';

  showMeasurePanel();

  // التأكد من تفعيل cross-snap على الأجزاء المرسومة
  ensureMeasureCrossSnap();

  // No separate sidebar manipulation needed here


  // على الجوال، ننتظر حتى يختار الأداة (مسافة/مساحة) قبل إغلاق القائمة وتشغيل العدسة
  if (!isMobile()) {
    addInteraction();
  }
}

let lastMagnifierUpdateTime = 0;
function updateCenterMagnifier() {
  if (!isMeasuring || !isMobile()) return;

  // ⚡ Throttle postrender updates to ~30fps
  const now = performance.now();
  if (now - lastMagnifierUpdateTime < 32) return;
  lastMagnifierUpdateTime = now;

  // لا نظهر العدسة إذا كانت ممنوعة (الشريط الجانبي مفتوح)
  if (window.magnifierSuppressed) return;

  const size = map.getSize();
  if (size) {
    const centerPx = [size[0] / 2, size[1] / 2];
    showMagnifierAt(centerPx);
    if (magnifier) magnifier.style.display = 'block';
    updateMobileToolValue();
  }
}

function stopMeasuring(button) {
  isMeasuring = false;
  window.GeoPST.state.isMeasuring = isMeasuring;
  button.removeClass('active');
  button.find('span').text('القياس');
  button.find('i').removeClass('fa-times').addClass('fa-ruler');

  hideMeasurePanel();
  removeInteraction();
  disableCoordinateClick();

  if (isMobile()) {
    toggleMobileControls(false);
    hideMagnifier();
    if (_measureMoveEndHandler) map.un('moveend', _measureMoveEndHandler);
    if (_measurePostRenderHandler) map.un('postrender', _measurePostRenderHandler);
    _measureMoveEndHandler = null;
    _measurePostRenderHandler = null;

    // استعادة الأزرار العائمة وشريط البحث فقط إذا الشريط الجانبي مغلق
    if (!$('#sidebar').hasClass('open')) {
      setFloatingControlsVisible(true);
    }
  }

  // No separate sidebar manipulation needed here

}

function cleanupMobileMeasureMode() {
  if (_measureMoveEndHandler) {
    map.un('moveend', _measureMoveEndHandler);
    _measureMoveEndHandler = null;
  }
  if (_measurePostRenderHandler) {
    map.un('postrender', _measurePostRenderHandler);
    _measurePostRenderHandler = null;
  }
  hideMagnifier();
  toggleMobileControls(false);
  window.magnifierSuppressed = false;
}

window.addEventListener('resize', () => {
  if (!isMobile()) {
    cleanupMobileMeasureMode();
  }
});

function closeMeasureCompletely() {
  isMeasuring = false;
  window.GeoPST.state.isMeasuring = isMeasuring;

  const btn = $('#measure');
  btn.removeClass('active');
  btn.find('span').text('القياس');
  btn.find('i').removeClass('fa-times').addClass('fa-ruler');

  removeInteraction();
  disableCoordinateClick();

  $('#measureModal').hide();
  $('#measureShowChip').hide();
}

/* =========================
   مودال أداة القياس
   ========================= */
function ensureMeasureModal() {
  if ($('#measureModal').length) return;

  const modalHtml = `
    <div id="measureModal" class="coordinate-modal" style="display:none" aria-modal="true" role="dialog">
      <div class="coordinate-modal-content">
        <div class="coordinate-modal-header">
          <h4><i class="fas fa-ruler-combined"></i> أدوات القياس</h4>
          <button class="coordinate-modal-close" id="measureModalClose" aria-label="إغلاق">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="coordinate-modal-body">
          <div id="measureModalContent"></div>
        </div>
        <div class="coordinate-modal-footer">
          <button type="button" class="coordinate-btn coordinate-btn-secondary" id="measureModalHide">
            <i class="fas fa-eye-slash"></i> إخفاء
          </button>
        </div>
      </div>
    </div>
  `;
  $('body').append(modalHtml);

  $('#measureModalClose').off('click').on('click', () => {
    closeMeasureCompletely();
  });

  $('#measureModalHide').off('click').on('click', () => {
    hideMeasurePanel();
  });

  $('#measureModal').on('click', function (e) {
    if (e.target === this) hideMeasurePanel();
  });
}

function showMeasurePanel() {
  // ensureMeasureModal();

  const panelHtml = `
    <div style="text-align:right;direction:rtl;">
      <div class="measure-control-group">
        <div class="section-header">
          <span>نوع القياس</span>
          <i class="fas fa-ruler-combined"></i>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
          <button class="modern-measure-btn active" data-type="LineString" id="measureLength">
            <i class="fas fa-ruler"></i><span>المسافة</span>
          </button>
          <button class="modern-measure-btn" data-type="Polygon" id="measureArea">
            <i class="fas fa-vector-square"></i><span>المساحة</span>
          </button>
        </div>
        <button class="modern-measure-btn" data-type="Point" id="measurePoint" style="width:100%;">
          <i class="fas fa-map-marker-alt"></i><span>إحداثيات النقاط</span>
        </button>
      </div>

      <div class="measure-control-group">
        <div class="section-header">
          <span>القياسات المحفوظة</span>
          <i class="fas fa-list-ul"></i>
        </div>
        <div class="measurements-container" id="measurementsList">
          <div style="text-align:center;color:var(--text-muted);padding:20px;">
            <i class="fas fa-ruler" style="font-size:2rem;margin-bottom:10px;opacity:.5;"></i>
            <p style="margin:0;font-size:.85rem;">لا توجد قياسات بعد.</p>
          </div>
        </div>
      </div>

      <div class="control-buttons">
        <button class="control-btn clear-btn" id="clearMeasurements">
          <i class="fas fa-trash-alt"></i><span>مسح الكل</span>
        </button>
        <button class="control-btn export-btn" id="exportMeasurements">
          <i class="fas fa-download"></i><span>تصدير</span>
        </button>
      </div>
    </div>
  `;

  $('#measureSectionContent').html(panelHtml);

  $('.modern-measure-btn').click(function () {
    $('.modern-measure-btn').removeClass('active');
    $(this).addClass('active');
    measureType = $(this).data('type');

    if (isMobile()) {
      closeSidebarOnMobile();
      toggleMobileControls(true);

      // Register magnifier update events so it refreshes during pan/zoom
      _measureMoveEndHandler = updateCenterMagnifier;
      _measurePostRenderHandler = updateCenterMagnifier;
      map.on('moveend', _measureMoveEndHandler);
      map.on('postrender', _measurePostRenderHandler);

      updateCenterMagnifier();
    }

    // Desktop logic removed to prevent sidebar desync

    if (measureType === 'Point') {
      removeInteraction();
      enableCoordinateClick();
      return;
    } else {
      disableCoordinateClick();
    }
    removeInteraction();
    addInteraction();

    // التأكد من تفعيل cross-snap على الأجزاء المرسومة
    ensureMeasureCrossSnap();
  });

  // ⚡ Fix Magnifier/Search Conflict
  const $searchInp = $('#mobileSearchInput');
  if ($searchInp.length) {
    $searchInp.on('focus', () => {
      window.magnifierSuppressed = true;
      hideMagnifier();
    });
    $searchInp.on('blur', () => {
      setTimeout(() => {
        window.magnifierSuppressed = false;
        if (isMeasuring && isMobile()) updateCenterMagnifier();
      }, 300);
    });
  }

  $('#clearMeasurements').click(clearAllMeasurements);
  $('#exportMeasurements').click(exportMeasurements);

  updateMeasurementsList();
}

function hideMeasurePanel() {
  $('#measureSection').slideUp(200);
}

/* =========================
   تفاعل الرسم (تمركز اللابل بالمنتصف)
   ========================= */
function addInteraction() {
  draw = new Draw({
    source: source,
    type: measureType,
    condition: (mapBrowserEvent) => {
      // على الجوال، نمنع النقر المباشر (Esri Style)
      if (isMobile()) return false;
      return true;
    },
    style: new Style({
      fill: new Fill({ color: 'rgba(220,53,69,0.12)' }),
      stroke: new Stroke({ color: '#3300ffff', lineDash: [10, 10], width: 3 }),
      image: new CircleStyle({
        radius: 5,
        stroke: new Stroke({ color: '#3300ffff', width: 2 }),
        fill: new Fill({ color: '#3300ffff' })
      })
    })
  });

  // إيقاف تتبع الموقع (Follow) عند بدء القياس لمنع تداخل حركة الخريطة
  if (window.GeoPST_Geolocation && typeof window.GeoPST_Geolocation.stopFollowing === 'function') {
    window.GeoPST_Geolocation.stopFollowing();
  }

  map.addInteraction(draw);

  // التأكد من تفعيل cross-snap على الأجزاء المرسومة
  ensureMeasureCrossSnap();

  // التأكد من أن snap interactions في الترتيب الصحيح (في النهاية)
  if (measureSnapInteraction) {
    map.removeInteraction(measureSnapInteraction);
    map.addInteraction(measureSnapInteraction);
  }
  if (measureSnapInteractionDraw) {
    map.removeInteraction(measureSnapInteractionDraw);
    map.addInteraction(measureSnapInteractionDraw);
  }

  createMeasureTooltip();
  createHelpTooltip();

  draw.on('drawstart', function (evt) {
    evt.stopPropagation();
    sketch = evt.feature;
    let tooltipCoord = evt.coordinate;

    listener = sketch.getGeometry().on('change', function (evt) {
      const geom = evt.target;
      let output;
      if (geom instanceof Polygon) {
        output = formatArea(geom);
        tooltipCoord = geom.getInteriorPoint().getCoordinates();
      } else if (geom instanceof LineString) {
        output = formatLength(geom);
        tooltipCoord = geom.getCoordinateAt(0.5);
      }
      measureTooltipElement.innerHTML = output;
      measureTooltip.setPosition(tooltipCoord);

      // تحديث القيمة في الشريط السفلي بشكل فوري (جوال وكمبيوتر)
      if ($('#mobileToolValue').length) {
        $('#mobileToolValue').text(output);
      }
    });
  });

  draw.on('drawend', function (evt) {
    evt.stopPropagation();
    const feature = evt.feature;
    const geom = feature.getGeometry();
    let output;
    let type;
    let finalPos;

    if (geom instanceof Polygon) {
      output = formatArea(geom);
      type = 'مساحة';
      finalPos = geom.getInteriorPoint().getCoordinates();
    } else if (geom instanceof LineString) {
      output = formatLength(geom);
      type = 'مسافة';
      finalPos = geom.getCoordinateAt(0.5);
    }

    measureTooltipElement.className = 'measure-tooltip-simple';
    measureTooltip.setOffset([0, -8]);
    measureTooltip.setPosition(finalPos);

    const measurement = {
      id: Date.now(),
      type: type,
      value: output,
      feature: feature,
      overlay: measureTooltip,
      timestamp: new Date().toLocaleString('ar-EG')
    };

    measurements.push(measurement);
    updateMeasurementsList();

    // التأكد من أن cross-snap يعمل على الأجزاء المرسومة بعد إضافة قياس جديد
    ensureMeasureCrossSnap();

    sketch = null;
    measureTooltipElement = null;
    createMeasureTooltip();
    unByKey(listener);
  });
}

function removeInteraction() {
  if (draw) {
    map.removeInteraction(draw);
    draw = null;
  }
  if (helpTooltip) {
    map.removeOverlay(helpTooltip);
    helpTooltip = null;
    helpTooltipElement = null;
  }
  if (measureTooltip) {
    map.removeOverlay(measureTooltip);
    measureTooltip = null;
    measureTooltipElement = null;
  }
}

/* =========================
   Tooltips
   ========================= */
function createHelpTooltip() {
  if (helpTooltipElement) helpTooltipElement.parentNode.removeChild(helpTooltipElement);
  helpTooltipElement = document.createElement('div');
  helpTooltipElement.className = 'ol-tooltip hidden';

  helpTooltip = new Overlay({
    element: helpTooltipElement,
    offset: [15, 0],
    positioning: 'center-left'
  });

  map.addOverlay(helpTooltip);
}

function createMeasureTooltip() {
  if (measureTooltipElement) measureTooltipElement.parentNode.removeChild(measureTooltipElement);
  measureTooltipElement = document.createElement('div');
  measureTooltipElement.className = 'measure-tooltip-simple';

  measureTooltip = new Overlay({
    element: measureTooltipElement,
    offset: [0, -8],
    positioning: 'bottom-center',
    stopEvent: false,
    insertFirst: false
  });

  map.addOverlay(measureTooltip);
}

/* =========================
   تنسيقات القيمة (Planar)
   ========================= */
function formatLength(line) {
  const length = line.getLength();
  return length > 1000 ? (length / 1000).toFixed(3) + ' كم' : length.toFixed(2) + ' م';
}

function formatArea(polygon) {
  const area = polygon.getArea();
  return area > 1000000 ? (area / 1000000).toFixed(4) + ' كم²' : area.toFixed(2) + ' م²';
}

/* =========================
   قائمة القياسات
   ========================= */
function updateMeasurementsList() {
  const listContainer = $('#measurementsList');

  if (measurements.length === 0) {
    listContainer.html(`
      <div style="text-align:center;color:#6c757d;padding:20px;">
        <i class="fas fa-ruler" style="font-size:2rem;margin-bottom:10px;opacity:.5;"></i>
        <p style="margin:0;font-size:.85rem;">لا توجد قياسات بعد.</p>
      </div>
    `);
    return;
  }

  let html = '';
  measurements.forEach((measurement, index) => {
    html += `
      <div class="measurement-item-modern">
        <div class="measurement-header">
          <span class="measurement-type">${measurement.type} ${index + 1}</span>
          <div class="measurement-actions">
            ${measurement.type !== 'إحداثيات' ? `
              <button class="action-btn zoom-btn" data-id="${measurement.id}" title="تكبير">
                <i class="fas fa-search-plus"></i>
              </button>` : ''}
            <button class="action-btn delete-btn" data-id="${measurement.id}" title="حذف">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="measurement-value">${measurement.value}</div>
      </div>
    `;
  });

  listContainer.html(html);

  // ⚡ Level 3: Event Delegation for measurement actions
  listContainer.off('click').on('click', '.action-btn', function (e) {
    const btn = $(this);
    const id = parseInt(btn.data('id'));
    if (btn.hasClass('zoom-btn')) {
      zoomToMeasurement(id);
    } else if (btn.hasClass('delete-btn')) {
      removeMeasurement(id);
    }
  });
}

window.removeMeasurement = function (id) {
  const idx = measurements.findIndex(m => m.id === id);
  if (idx === -1) return;

  const measurement = measurements[idx];

  if (measurement.type === 'إحداثيات' && measurement.pointNumber) {
    const pIdx = coordinatePoints.findIndex(p => p.pointNumber === measurement.pointNumber);
    if (pIdx !== -1) {
      const point = coordinatePoints[pIdx];
      if (point.feature) source.removeFeature(point.feature);
      if (point.overlay) map.removeOverlay(point.overlay);
      coordinatePoints.splice(pIdx, 1);
    }
  } else {
    if (measurement.feature) source.removeFeature(measurement.feature);
    if (measurement.overlay) map.removeOverlay(measurement.overlay);
  }

  measurements.splice(idx, 1);
  updateMeasurementsList();
};

window.zoomToMeasurement = function (id) {
  const measurement = measurements.find(m => m.id === id);
  if (measurement && measurement.feature) {
    const extent = measurement.feature.getGeometry().getExtent();
    map.getView().fit(extent, { duration: 500, padding: [50, 50, 50, 50], maxZoom: 11 });
  }
};

function clearAllMeasurements() {
  source.clear();
  measurements = [];

  coordinatePoints.forEach(point => {
    if (point.overlay) map.removeOverlay(point.overlay);
  });
  coordinatePoints = [];
  pointCounter = 1;

  updateMeasurementsList();

  map.getOverlays().getArray().slice().forEach(overlay => {
    const el = overlay.getElement();
    if (!el) return;
    const cls = el.className || '';
    if (
      cls.includes('ol-tooltip-static') ||
      cls.includes('ol-tooltip-measure') ||
      cls.includes('measure-tooltip-simple') ||
      cls.includes('coordinate-point-tooltip')
    ) {
      map.removeOverlay(overlay);
    }
  });
}

function exportMeasurements() {
  if (measurements.length === 0) {
    const errorHtml = `
      <div style="text-align:right;direction:rtl;">
        <h4 style="color:#dc3545;margin-bottom:15px;">
          <i class="fas fa-exclamation-triangle"></i> لا توجد قياسات
        </h4>
        <p style="margin-bottom:10px;line-height:1.5;">
          لا توجد قياسات لتصديرها. يرجى إجراء بعض القياسات أولاً.
        </p>
      </div>
    `;
    if ($("#errorModal").length && $("#errorMessage").length) {
      $("#errorMessage").html(errorHtml);
      $("#errorModal").show();
    } else {
      alert('لا توجد قياسات لتصديرها.');
    }
    return;
  }

  let exportText = 'تقرير القياسات\n================\n\n';
  measurements.forEach((m, i) => {
    exportText += `${m.type} ${i + 1}:\nالقيمة: ${m.value}\nالتاريخ: ${m.timestamp}\n\n`;
  });
  exportText += `إجمالي القياسات: ${measurements.length}\n`;
  exportText += `تاريخ التصدير: ${new Date().toLocaleString('ar-EG')}\n`;

  const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `measurements_${new Date().toISOString().split('T')[0]}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* =========================
   مساعدة تفاعلية أثناء الرسم
   ========================= */
map.on('pointermove', function (evt) {
  if (!isMeasuring) return; // Guard for non-active state
  if (evt.dragging || isMobile()) {
    if (helpTooltipElement) helpTooltipElement.classList.add('hidden');
    return;
  }

  let helpMsg = 'انقر لبدء الرسم';
  if (measureType === 'Point') {
    helpMsg = 'انقر لتحديد إحداثيات النقطة';
  } else if (sketch) {
    const geom = sketch.getGeometry();
    if (geom instanceof Polygon) helpMsg = 'انقر لإضافة نقطة، انقر نقرة مزدوجة لإنهاء المضلع';
    else if (geom instanceof LineString) helpMsg = 'انقر لإضافة نقطة، انقر نقرة مزدوجة لإنهاء الخط';
  }

  if (helpTooltipElement) {
    helpTooltipElement.innerHTML = helpMsg;
    helpTooltip.setPosition(evt.coordinate);
    helpTooltipElement.classList.remove('hidden');
  }
});

/* =========================
   وضع إحداثيات النقاط
   ========================= */
let coordinateClickListener = null;

function enableCoordinateClick() {
  if (coordinateClickListener) return;
  coordinateClickListener = map.on('singleclick', function (evt) {
    // على الجوال، نعتمد على زر الإضافة اليدوي والعدسة المركزية للدقة
    if (isMobile()) return;

    const coords = evt.coordinate;

    const transformed = safeTransform(coords, map.getView().getProjection(), 'EPSG:28191');
    const x = Math.round(transformed[0] * 100) / 100;
    const y = Math.round(transformed[1] * 100) / 100;

    createPermanentCoordinatePoint(coords, x, y, pointCounter);
    saveCoordinateMeasurement(x, y, pointCounter);
    pointCounter++;
  });
}

function disableCoordinateClick() {
  if (coordinateClickListener) {
    unByKey(coordinateClickListener);
    coordinateClickListener = null;
  }
}

function createPermanentCoordinatePoint(coord, x, y, pointNumber) {
  const pointFeature = new Feature({
    geometry: new Point(coord),
    pointNumber: pointNumber,
    coordinates: { x: x, y: y }
  });

  pointFeature.setStyle(
    new Style({
      image: new CircleStyle({
        radius: 6,
        fill: new Fill({ color: '#3300ffff' }),
        stroke: new Stroke({ color: '#fff', width: 2 })
      })
    })
  );

  source.addFeature(pointFeature);

  const tooltipElement = document.createElement('div');
  tooltipElement.className = 'coordinate-point-tooltip';
  tooltipElement.innerHTML = `
    <div class="point-number">${pointNumber}</div>
    <div class="point-coordinates">X: ${x}<br>Y: ${y}</div>
  `;

  const overlay = new Overlay({
    element: tooltipElement,
    position: coord,
    positioning: 'bottom-center',
    offset: [0, -2],
    stopEvent: false
  });
  map.addOverlay(overlay);

  coordinatePoints.push({
    id: Date.now(),
    pointNumber: pointNumber,
    feature: pointFeature,
    overlay: overlay,
    coordinates: { x: x, y: y }
  });
}

function saveCoordinateMeasurement(x, y, pointNumber) {
  const measurement = {
    id: Date.now(),
    type: 'إحداثيات',
    value: `النقطة ${pointNumber}: X: ${x}, Y: ${y}`,
    pointNumber: pointNumber,
    coordinates: { x: x, y: y },
    feature: null,
    timestamp: new Date().toLocaleString('ar-EG')
  };
  measurements.push(measurement);
  updateMeasurementsList();
}

// للاستخدام الاختياري
function showCoordinateTooltip(coord, x, y) {
  if (measureTooltipElement) {
    measureTooltipElement.parentNode.removeChild(measureTooltipElement);
  }
  measureTooltipElement = document.createElement('div');
  measureTooltipElement.className = 'measure-tooltip-simple';
  measureTooltipElement.innerHTML = `
    <div class="coordinate-title"><i class="fas fa-map-marker-alt"></i> الإحداثيات</div>
    <div class="coordinate-values">
      <div><strong>X:</strong> ${x}</div>
      <div><strong>Y:</strong> ${y}</div>
    </div>
  `;
  measureTooltip = new Overlay({
    element: measureTooltipElement,
    offset: [0, -20],
    positioning: 'bottom-center',
    stopEvent: false,
    insertFirst: false
  });
  map.addOverlay(measureTooltip);
  measureTooltip.setPosition(coord);
}

/* =========================
   تحويلات آمنة
   ========================= */
function safeTransform(coords, sourceProj, targetProj) {
  try {
    const getCode = (p) => (typeof p === 'string' ? p : (p && typeof p.getCode === 'function' ? p.getCode() : null));
    const src = getCode(sourceProj);
    const dst = getCode(targetProj) || targetProj;
    if (!src || !dst) return coords;
    return transform(coords, src, dst);
  } catch (e) {
    console.error('فشل التحويل:', e);
    return coords;
  }
}

$('#measureModal').off('click').on('click', function (e) {
  if (e.target === this) hideMeasurePanel();
});

// ✅ [INTERACTION VACUUM] -Lifecycle cleanup when switching tools
$(document).on('tool:cleanup', (e, data) => {
  if (data.id !== 'measure') {
    // If another tool started, clear our temporary visuals
    helpTooltipElement?.parentNode?.removeChild(helpTooltipElement);
    helpTooltipElement = null;
    hideMagnifier();
    // Stop any active drawing/measure interaction
    if (isMeasuring) {
      removeInteraction();
      isMeasuring = false;
      $('#measure').removeClass('active');
    }
  }
});

/* =========================
   تصدير
   ========================= */
// جعل source متاحاً عالمياً للـ cross-snap
window.measureSourceForSnap = () => source;

// Expose to window for global access
window.updateCenterMagnifier = updateCenterMagnifier;

export { source, vector };  